import { createFileRoute, Link } from "@tanstack/react-router";
import {
  GraduationCap,
  Users,
  BookOpen,
  Scale,
  Handshake,
  Trophy,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import { site } from "@/lib/site";

export const Route = createFileRoute("/education/high-school")({
  head: () => ({
    meta: [
      {
        title:
          "NDLCS Academy & High School Legal Literacy Initiative | Nkadimeng Danny",
      },
      {
        name: "description",
        content:
          "Building legally aware communities through education, skills development and compliance. Debate leagues, online courses, community workshops and skills pathways for South African youth.",
      },
      {
        property: "og:title",
        content:
          "NDLCS Academy & High School Legal Literacy Initiative",
      },
      {
        property: "og:description",
        content:
          "A community education initiative promoting legal literacy, compliance awareness and workplace readiness among young South Africans.",
      },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://nkadimengdannylcs.com/education/high-school",
      },
    ],
  }),
  component: HighSchoolInitiative,
});

const pillars = [
  {
    icon: Trophy,
    title: "High School Legal Debate League",
    body: "Structured debate programme where learners engage with contemporary legal and civic issues.",
    points: [
      "Public speaking, critical thinking, research and leadership",
      "Teamwork, constitutional awareness, ethical decision-making",
    ],
    topics: [
      "Constitutional rights",
      "Cybercrime & online safety",
      "Consumer protection",
      "Labour rights",
      "Human rights",
      "Environmental responsibility",
      "AI & ethics",
      "Social media accountability",
      "GBV awareness",
      "Community justice",
    ],
  },
  {
    icon: BookOpen,
    title: "NDLCS Academy Online Learning",
    body: "Free access to professionally designed online courses covering practical legal and compliance topics.",
    points: [
      "Learning modules, interactive quizzes and practical assessments",
      "Final examination with administrative review",
      "Tamper-evident digital certificate on successful completion",
    ],
    topics: [
      "Introduction to South African Law",
      "The Constitution",
      "Human Rights",
      "Consumer Rights",
      "Labour Rights",
      "POPIA Awareness",
      "Cyber Safety",
      "Business Compliance",
      "Workplace Ethics",
      "Corporate Governance",
      "Entrepreneurship Fundamentals",
      "Financial Literacy",
      "Communication Skills",
      "Professional Conduct",
      "Introduction to Legal Research",
    ],
  },
  {
    icon: GraduationCap,
    title: "Skills Development Pathway",
    body: "A structured route from school education into further skills development and opportunity.",
    points: [
      "School Education → Legal Awareness Workshops",
      "Debate Programme → NDLCS Academy Courses",
      "Digital Certification → Career Guidance",
      "Partnerships with accredited providers for NQF-aligned programmes",
      "Progression into employment or entrepreneurship",
    ],
  },
  {
    icon: Users,
    title: "Community Legal Literacy",
    body: "Outreach workshops delivered to schools, community organisations and small businesses.",
    points: [
      "Compliance awareness and legal documentation",
      "Responsible digital citizenship and consumer protection",
      "Employment rights, business governance, community leadership",
      "Ethics and accountability",
    ],
  },
];

const challenges = [
  "The Constitution and the Bill of Rights",
  "Consumer rights",
  "Employment rights",
  "Digital safety and cyber responsibility",
  "Privacy and POPIA awareness",
  "Contracts and everyday legal obligations",
  "Business compliance",
  "Workplace ethics",
  "Financial responsibility",
  "Public speaking and critical thinking",
];

const partnerBenefits = [
  "Programme branding and recognition",
  "Visibility at debate competitions and educational events",
  "Recognition on learning materials where appropriate",
  "Participation in learner engagement activities",
  "Annual programme impact reporting",
  "Alignment with CSI, ESG and SED objectives",
];

const measures = [
  "Schools participating",
  "Learners reached",
  "Debate competitions conducted",
  "Courses completed",
  "Certificates issued",
  "Community workshops delivered",
  "Youth progressing into further education / accredited skills",
  "Volunteer participation",
  "Community engagement initiatives",
];

function HighSchoolInitiative() {
  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="border-b border-border bg-gradient-to-b from-navy/5 to-transparent">
        <div className="container-narrow py-16 md:py-24">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">
            Education Division · Community Initiative
          </p>
          <h1 className="mt-3 font-display text-4xl leading-tight text-navy md:text-5xl">
            NDLCS Academy &amp; High School
            <span className="text-gold"> Legal Literacy Initiative</span>
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-foreground/80">
            Building legally aware communities through education, skills
            development and compliance.
          </p>
          <p className="mt-4 max-w-3xl text-sm text-muted-foreground">
            {site.name} · Reg {site.registration.number}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-medium text-navy-foreground hover:bg-navy/90"
            >
              Partner with us <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/resources"
              className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-5 py-3 text-sm font-medium hover:border-navy"
            >
              Browse learning resources
            </Link>
            <Link
              to="/education"
              className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-5 py-3 text-sm font-medium hover:border-navy"
            >
              ← Back to Education hub
            </Link>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="container-narrow py-14">
        <h2 className="font-display text-2xl text-navy md:text-3xl">
          About the initiative
        </h2>
        <div className="mt-4 space-y-4 text-foreground/85 leading-relaxed">
          <p>
            Nkadimeng Danny Legal and Compliance Support (NDLCS) is a South
            African legal support and compliance practice committed to making
            professional legal support, legal research, compliance guidance and
            business documentation more accessible to individuals, entrepreneurs,
            organisations and communities.
          </p>
          <p>
            Our work focuses on helping people understand legal processes,
            strengthen compliance, prepare professional documentation and make
            informed decisions through practical guidance and education. We do
            not provide legal representation; instead, we assist clients through
            research, documentation, compliance support and educational
            initiatives that promote lawful and informed participation in
            society.
          </p>
          <p>
            Our vision extends beyond assisting businesses and individuals. We
            believe legal knowledge should begin at an early age, empowering
            young people to become responsible citizens, ethical leaders and
            informed decision-makers.
          </p>
        </div>
      </section>

      {/* Challenge */}
      <section className="border-y border-border bg-muted/30">
        <div className="container-narrow py-14">
          <h2 className="font-display text-2xl text-navy md:text-3xl">
            The challenge
          </h2>
          <p className="mt-3 max-w-3xl text-foreground/80">
            Many South African learners complete their schooling without
            practical knowledge of:
          </p>
          <ul className="mt-6 grid gap-3 text-sm text-foreground/85 md:grid-cols-2">
            {challenges.map((c) => (
              <li
                key={c}
                className="flex items-start gap-2 rounded border border-border bg-card p-3"
              >
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                {c}
              </li>
            ))}
          </ul>
          <p className="mt-6 max-w-3xl text-sm text-muted-foreground">
            This knowledge gap contributes to poor decision-making, cybercrime,
            exploitation, misinformation, workplace disputes and missed
            opportunities for personal and professional growth.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="container-narrow py-14">
        <h2 className="font-display text-2xl text-navy md:text-3xl">
          Our solution — four integrated pillars
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          NDLCS Academy combines legal education, practical skills development
          and community engagement.
        </p>
        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {pillars.map((p) => (
            <article
              key={p.title}
              className="rounded-xl border border-border bg-card p-6"
            >
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-md bg-navy/5 text-navy">
                  <p.icon className="h-5 w-5" />
                </span>
                <h3 className="font-display text-lg text-navy">{p.title}</h3>
              </div>
              <p className="mt-3 text-sm text-foreground/80 leading-relaxed">
                {p.body}
              </p>
              <ul className="mt-4 space-y-1.5 text-sm text-foreground/80">
                {p.points.map((pt) => (
                  <li key={pt} className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
              {p.topics ? (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {p.topics.map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-border bg-background px-2.5 py-0.5 text-[11px] text-foreground/70"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </section>

      {/* Why partner */}
      <section className="border-y border-border bg-navy text-navy-foreground">
        <div className="container-narrow py-14">
          <div className="flex items-center gap-3">
            <Handshake className="h-6 w-6 text-gold" />
            <h2 className="font-display text-2xl md:text-3xl">
              Why partner with NDLCS?
            </h2>
          </div>
          <p className="mt-3 max-w-3xl text-navy-foreground/85">
            Partnerships help us improve legal literacy, expand access to
            practical education, promote ethical leadership, strengthen
            compliance awareness, enhance employability, encourage responsible
            digital citizenship, support entrepreneurship and build stronger,
            more informed communities.
          </p>
          <div className="mt-8 grid gap-3 md:grid-cols-2">
            {partnerBenefits.map((b) => (
              <div
                key={b}
                className="flex items-start gap-2 rounded border border-navy-foreground/15 bg-navy-foreground/5 p-3 text-sm"
              >
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                {b}
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-gold px-5 py-3 text-sm font-medium text-navy hover:bg-gold/90"
            >
              Become a partner <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="container-narrow py-14">
        <div className="flex items-center gap-3 text-navy">
          <Scale className="h-6 w-6 text-gold" />
          <h2 className="font-display text-2xl md:text-3xl">Measuring impact</h2>
        </div>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground">
          The programme monitors and reports on measurable outcomes each year.
        </p>
        <ul className="mt-6 grid gap-3 text-sm text-foreground/85 md:grid-cols-3">
          {measures.map((m) => (
            <li
              key={m}
              className="flex items-start gap-2 rounded border border-border bg-card p-3"
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              {m}
            </li>
          ))}
        </ul>
      </section>

      {/* Approach */}
      <section className="border-t border-border bg-muted/30">
        <div className="container-narrow py-14">
          <h2 className="font-display text-2xl text-navy md:text-3xl">
            Our approach to partnerships
          </h2>
          <div className="mt-4 space-y-4 text-foreground/85 leading-relaxed">
            <p>
              We do not simply seek sponsorship; we seek long-term partnerships
              with organisations that share our commitment to education,
              responsible citizenship and sustainable community development.
            </p>
            <p>
              Every contribution directly supports learner development through
              educational resources, workshops, debate competitions, digital
              learning, mentorship opportunities and skills development pathways.
            </p>
            <p className="text-navy font-medium">
              Together, we can help build a generation of informed citizens who
              understand their rights, respect their responsibilities and
              contribute positively to South Africa's future.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-medium text-navy-foreground hover:bg-navy/90"
            >
              Talk to us about a partnership <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-5 py-3 text-sm font-medium hover:border-navy"
            >
              Book a consultation
            </Link>
          </div>
        </div>
      </section>

      <section className="container-narrow py-10 text-xs text-muted-foreground">
        <p className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          <span>
            A practice built on purpose — empowering communities through
            knowledge, compliance and opportunity. {site.name} · Reg{" "}
            {site.registration.number}.
          </span>
        </p>
      </section>
    </div>
  );
}
